import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SearchComponent } from "../../../../shared/search/search.component";
import { InstitutionFilterComponent } from "../../../../shared/filter/institution-filter/institution-filter.component";
import {
  SingleProductRepaymentTableComponent
} from "../../../../components/card/product-management/single-product-repayment-table/single-product-repayment-table.component";
import { PaginationComponent } from "../../../../components/pagination/pagination.component";
import { ProgressLoaderComponent } from "../../../../components/loaders/progress-loader/progress-loader.component";
import { ConfirmationDialogComponent } from "../../../../components/shared/confirmation-dialog/confirmation-dialog.component";
import { CustomInputComponent } from "../../../../components/input/custom-input/custom-input.component";
import { CustomSelectInputComponent } from "../../../../components/input/custom-select-input/custom-select-input.component";
import { ImagesService } from '../../../../services/general/images.service';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ProductDetailsStore } from '../product-details.store';
import { LoanService } from '../../../../services/loan/loan.service';
import { PermissionService } from '../../../../services/permission/permission.service';
import { ToastService } from '../../../../services/general/toast.service';
import { DataImport, Loan, LoanRepaymentDto } from '../../../../interfaces/responses/loan.interface';
import { Subject, Subscription, debounceTime, distinctUntilChanged, switchMap, timer } from 'rxjs';

@Component({
  selector: 'app-repayments',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SearchComponent,
    InstitutionFilterComponent,
    SingleProductRepaymentTableComponent,
    PaginationComponent,
    ProgressLoaderComponent,
    ConfirmationDialogComponent,
    CustomInputComponent,
    CustomSelectInputComponent,
  ],
  templateUrl: './repayments.component.html',
  styleUrl: './repayments.component.scss'
})
export class RepaymentsComponent implements OnInit {
  _images = inject(ImagesService);
  today = new Date().toISOString().split('T')[0];
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  protected productDetailsStore = inject(ProductDetailsStore);
  private loanService = inject(LoanService);
  permissionService = inject(PermissionService);
  private _toast = inject(ToastService);
  private _fb = inject(FormBuilder);

  @ViewChild('uploadFileInput') uploadFileInputRef!: ElementRef;

  itemsPerPage = 10;
  currentPage = 1;
  totalItems = 0;
  productId = '';
  keyword = '';
  filterStartDate: string | undefined;
  filterEndDate: string | undefined;
  filterStatus: string | undefined;

  // Repayments have no server-side status filter, so an active status
  // filter is applied client-side against the full (unpaginated) result set.
  private allFetchedRepayments: LoanRepaymentDto[] = [];
  private clientFilteredRepayments: LoanRepaymentDto[] = [];

  get isStatusFilterActive(): boolean {
    return !!this.filterStatus;
  }

  loanStatusOptions = [
    { value: 'PENDING_APPROVAL',     label: 'Pending' },
    { value: 'APPROVED',             label: 'Approved' },
    { value: 'ACCEPTED',             label: 'Accepted' },
    { value: 'COUNTERED',            label: 'Countered' },
    { value: 'PROCESSING',           label: 'Processing' },
    { value: 'PENDING_DISBURSEMENT', label: 'Pending Disbursement' },
    { value: 'DISBURSED',            label: 'Disbursed' },
    { value: 'ACTIVE',               label: 'Active' },
    { value: 'REJECTED',             label: 'Rejected' },
    { value: 'DEFAULTED',            label: 'Defaulted' },
    { value: 'COMPLETED',            label: 'Completed' },
    { value: 'CANCELLED',            label: 'Cancelled' },
    { value: 'COUNTER_REJECTED',     label: 'Ctr. Rejected' },
  ];

  repayments: LoanRepaymentDto[] = [];
  isLoading = false;

  private searchSubject = new Subject<string>();

  // Record repayment modal
  showRecordModal = false;
  isSubmitting = false;
  isLookingUp = false;
  lookupError = '';
  resolvedLoan: Loan | null = null;
  repaymentTypes = [
    { label: 'Bank', value: 'BANK' },
    { label: 'Cash', value: 'CASH' },
    { label: 'Mobile Money', value: 'MOMO' },
  ];

  recordForm: FormGroup = this._fb.group({
    loanReference: ['', Validators.required],
    loanId: ['', Validators.required],
    type: ['', Validators.required],
    transactionId: ['', Validators.required],
    amount: ['', [Validators.required, Validators.min(0.01)]],
    date: ['', Validators.required],
    comment: [''],
  });

  // Upload modal
  showUploadModal = false;
  uploadFile: File | null = null;
  isUploading = false;
  uploadFieldError = '';
  uploadPhase: 'idle' | 'uploading' | 'processing' | 'result' = 'idle';
  importBatchId: string | null = null;
  importResult: DataImport | null = null;
  pollTimedOut = false;
  pollError = '';
  isRechecking = false;
  readonly csvTemplateHeaders = ['loanReference', 'amount', 'date', 'type', 'transactionId', 'comment'];
  private pollSub: Subscription | null = null;
  private pollStartedAt = 0;
  private readonly POLL_INTERVAL_MS = 4000;
  private readonly POLL_TIMEOUT_MS = 5 * 60 * 1000;
  private readonly MAX_UPLOAD_BYTES = 3 * 1024 * 1024;

  // Delete confirmation
  showDeleteDialog = false;
  pendingDeleteId: string | null = null;

  constructor() {
    toObservable(this.productDetailsStore.productId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        if (id) {
          this.productId = id;
          this.loadRepayments();
        }
      });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.currentPage = 1;
      this.loadRepayments();
    });

    this.destroyRef.onDestroy(() => this.stopPolling());
  }

  ngOnInit(): void {}

  loadRepayments(): void {
    if (this.isStatusFilterActive) {
      this.loadAndFilterRepaymentsByStatus();
      return;
    }

    this.isLoading = true;
    this.loanService.getRepayments({
      page: this.currentPage,
      perPage: this.itemsPerPage,
      paginate: true,
      keyword: this.keyword || undefined,
      productId: this.productId || undefined,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate,
    }).subscribe({
      next: (res: any) => {
        if (Array.isArray(res)) {
          this.repayments = res;
          this.totalItems = res.length;
        } else {
          this.repayments = res.data ?? [];
          this.totalItems = res.meta?.total ?? 0;
        }
        this.isLoading = false;
      },
      error: () => {
        this._toast.error('Failed to load repayments');
        this.isLoading = false;
      }
    });
  }

  private loadAndFilterRepaymentsByStatus(): void {
    this.isLoading = true;
    this.loanService.getRepayments({
      paginate: false,
      keyword: this.keyword || undefined,
      productId: this.productId || undefined,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate,
    }).subscribe({
      next: (res: any) => {
        this.allFetchedRepayments = Array.isArray(res) ? res : (res.data ?? []);
        this.applyStatusFilter();
        this.isLoading = false;
      },
      error: () => {
        this._toast.error('Failed to load repayments');
        this.isLoading = false;
      }
    });
  }

  private applyStatusFilter(): void {
    this.clientFilteredRepayments = this.allFetchedRepayments.filter(r =>
      (r.loan?.status || '').toUpperCase() === this.filterStatus
    );
    this.totalItems = this.clientFilteredRepayments.length;
    const start = (this.currentPage - 1) * this.itemsPerPage;
    this.repayments = this.clientFilteredRepayments.slice(start, start + this.itemsPerPage);
  }

  filterRepayments(formData: FormGroup) {
    const v = formData.value;
    this.filterStartDate = v.startDate || undefined;
    this.filterEndDate   = v.endDate   || undefined;
    this.filterStatus    = v.status    || undefined;
    this.currentPage = 1;
    this.loadRepayments();
  }

  resetRepaymentFilters() {
    this.filterStartDate = undefined;
    this.filterEndDate   = undefined;
    this.filterStatus    = undefined;
    this.currentPage = 1;
    this.loadRepayments();
  }

  onSearch(value: string): void {
    this.keyword = value;
    this.searchSubject.next(value);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    if (this.isStatusFilterActive) {
      this.applyStatusFilter();
    } else {
      this.loadRepayments();
    }
  }

  // Record repayment
  openRecordModal(): void {
    this.showRecordModal = true;
  }

  closeRecordModal(): void {
    this.showRecordModal = false;
    this.resolvedLoan = null;
    this.lookupError = '';
    this.recordForm.reset();
    this.recordForm.markAsPristine();
    this.recordForm.markAsUntouched();
  }

  lookupLoan(): void {
    const ref = this.recordForm.get('loanReference')?.value?.trim();
    if (!ref) return;
    this.isLookingUp = true;
    this.lookupError = '';
    this.resolvedLoan = null;
    this.recordForm.get('loanId')?.setValue('');
    this.loanService.getPaginatedProductLoans({ keyword: ref, paginate: true, perPage: 20, productId: this.productId || undefined }).subscribe({
      next: (res: any) => {
        const loans: Loan[] = Array.isArray(res) ? res : (res.data ?? []);
        const found = loans.find((l: Loan) => l.reference === ref);
        if (found) {
          this.resolvedLoan = found;
          this.recordForm.get('loanId')?.setValue(found.id);
        } else {
          this.lookupError = 'No loan found with that reference';
        }
        this.isLookingUp = false;
      },
      error: () => {
        this.lookupError = 'Lookup failed. Please try again.';
        this.isLookingUp = false;
      }
    });
  }

  submitRepayment(): void {
    if (this.recordForm.invalid) {
      this.recordForm.markAllAsTouched();
      return;
    }
    if (!this.resolvedLoan) {
      this.lookupError = 'Please look up a valid loan reference first';
      return;
    }
    this.isSubmitting = true;
    const { loanReference, type, transactionId, amount, date, comment } = this.recordForm.value;
    this.loanService.createRepayment({ loanId: loanReference, type, transactionId, amount: +amount, date, comment }).subscribe({
      next: () => {
        this._toast.success('Repayment recorded successfully');
        this.closeRecordModal();
        this.loadRepayments();
        this.isSubmitting = false;
      },
      error: (err) => {
        this._toast.error(err?.message || 'Failed to record repayment');
        this.isSubmitting = false;
      }
    });
  }

  // Delete
  onDeleteRequest(id: string): void {
    this.pendingDeleteId = id;
    this.showDeleteDialog = true;
  }

  confirmDelete(): void {
    if (!this.pendingDeleteId) return;
    this.loanService.deleteRepayment(this.pendingDeleteId).subscribe({
      next: () => {
        this._toast.success('Repayment deleted');
        this.showDeleteDialog = false;
        this.pendingDeleteId = null;
        this.loadRepayments();
      },
      error: () => {
        this._toast.error('Failed to delete repayment');
        this.showDeleteDialog = false;
      }
    });
  }

  cancelDelete(): void {
    this.showDeleteDialog = false;
    this.pendingDeleteId = null;
  }

  // Upload
  openUploadModal(): void {
    this.resetUploadState();
    this.showUploadModal = true;
  }

  closeUploadModal(): void {
    if (this.uploadPhase === 'processing') {
      this._toast.info('File is still processing on the server. Refresh the repayments list later to see the results.');
    }
    this.showUploadModal = false;
    this.resetUploadState();
  }

  private resetUploadState(): void {
    this.stopPolling();
    this.uploadFile = null;
    this.isUploading = false;
    this.uploadFieldError = '';
    this.uploadPhase = 'idle';
    this.importBatchId = null;
    this.importResult = null;
    this.pollTimedOut = false;
    this.pollError = '';
    this.isRechecking = false;
    if (this.uploadFileInputRef?.nativeElement) {
      this.uploadFileInputRef.nativeElement.value = '';
    }
  }

  onUploadFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files?.length) {
      this.uploadFile = target.files[0];
      this.uploadFieldError = '';
      if (!this.uploadFile.name.toLowerCase().endsWith('.csv')) {
        this.uploadFieldError = 'Only CSV files are accepted';
      } else if (this.uploadFile.size > this.MAX_UPLOAD_BYTES) {
        this.uploadFieldError = 'File is too large (max 3MB)';
      }
    }
  }

  submitUpload(): void {
    if (!this.uploadFile || this.uploadFieldError) return;
    this.isUploading = true;
    this.uploadPhase = 'uploading';
    this.loanService.uploadRepayments(this.uploadFile).subscribe({
      next: (res) => {
        this.isUploading = false;
        this.importBatchId = res.id;
        if (res.id) {
          this.startPolling(res.id);
        } else {
          // Older backend without batch ids — fall back to fire-and-forget.
          this._toast.success('Repayments uploaded successfully');
          this.closeUploadModal();
          this.loadRepayments();
        }
      },
      error: (err) => {
        this.isUploading = false;
        this.uploadPhase = 'idle';
        this.uploadFieldError = err?.error?.message || 'Failed to upload repayments';
      }
    });
  }

  private startPolling(batchId: string): void {
    this.stopPolling();
    this.uploadPhase = 'processing';
    this.pollStartedAt = Date.now();
    this.pollSub = timer(0, this.POLL_INTERVAL_MS).pipe(
      switchMap(() => this.loanService.getDataImport(batchId))
    ).subscribe({
      next: (res) => {
        if (res.status !== 'PROCESSING') {
          this.stopPolling();
          this.onImportFinished(res);
        } else if (Date.now() - this.pollStartedAt > this.POLL_TIMEOUT_MS) {
          this.stopPolling();
          this.pollTimedOut = true;
          this.uploadPhase = 'result';
        }
      },
      error: (err) => {
        this.stopPolling();
        this.pollError = err?.status === 404
          ? 'Batch not found'
          : 'Failed to check processing status';
        this.uploadPhase = 'result';
      }
    });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private onImportFinished(res: DataImport): void {
    this.importResult = res;
    this.pollTimedOut = false;
    this.pollError = '';
    this.uploadPhase = 'result';
    if (res.success > 0) {
      this.loadRepayments();
    }
  }

  // One-off re-check after a polling timeout.
  recheckImport(): void {
    if (!this.importBatchId || this.isRechecking) return;
    this.isRechecking = true;
    this.loanService.getDataImport(this.importBatchId).subscribe({
      next: (res) => {
        this.isRechecking = false;
        if (res.status !== 'PROCESSING') {
          this.onImportFinished(res);
        }
      },
      error: (err) => {
        this.isRechecking = false;
        this.pollError = err?.status === 404
          ? 'Batch not found'
          : 'Failed to check processing status';
      }
    });
  }

  get importOutcome(): 'success' | 'partial' | 'failure' | null {
    const r = this.importResult;
    if (!r) return null;
    if (r.status === 'FAILED' || (r.total > 0 && r.failed === r.total)) return 'failure';
    if (r.status === 'PARTIAL' || r.failed > 0) return 'partial';
    return 'success';
  }

  get visibleRowErrors() {
    return this.importResult?.rowErrors ?? [];
  }

  get hiddenErrorCount(): number {
    const r = this.importResult;
    if (!r?.rowErrors) return 0;
    return Math.max(0, r.failed - r.rowErrors.length);
  }

  downloadTemplate(): void {
    const csv = [
      this.csvTemplateHeaders.join(','),
      'LN-2024-0001,1200.50,2026-10-01,CASH,TXN-88821,october instalment',
    ].join('\n');
    this.downloadCsv(csv, 'repayments-template.csv');
  }

  downloadErrorsCsv(): void {
    const errors = this.visibleRowErrors;
    if (!errors.length) return;
    const escape = (v: string | number | null) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      'row,loanReference,error',
      ...errors.map(e => [e.row, escape(e.reference), escape(e.error)].join(',')),
    ].join('\n');
    this.downloadCsv(csv, 'repayment-upload-errors.csv');
  }

  private downloadCsv(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}
